// Talent Management > Preferences: standing boundaries a talent set, waiting on us.
//
// THE GATE, not a report. A talent tells STERLING "nothing under $500" or "no more logo
// jobs", approves the exact wording, and the row lands here (migration 0175). It filters
// NOTHING until somebody on this page approves it. Only then does it become that talent's
// one `active` preference and start removing them from briefs that do not fit.
//
// That human step is the whole point, for two reasons a summary row cannot show. The
// capture is model-authored off one conversational turn, so a misread is possible in a way
// a form submission is not: "no logo work" from someone venting about ONE bad logo brief
// becomes a standing boundary that outlives the project. And the cost of a wrong one is
// invisible from every direction — the talent sees fewer briefs and cannot tell why, the
// client never learns somebody was filtered out, and no error is raised anywhere.
//
// Hence the conversation link on every row. Approving one of these without reading what the
// talent actually said is rubber-stamping, so the talent's name goes straight to the
// STERLING thread the capture came from.
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  SortableTh,
  TableExport,
  usePagedRows,
  useTableSort,
} from "@recruiter/shared";
import type { SellerPreferenceEditRequest, SellerPreferenceRow } from "@recruiter/shared/types";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";

import { AreaBoundary } from "../components/area-gate";
import { ExplorerTalentLink } from "../components/explorer-link";
import { SectionTabs, TALENT_TABS } from "../components/section-tabs";
import { ShowMoreRow } from "../components/show-more-row";
import { EmptyState, TableSkeleton } from "../components/states";
import {
  ApiError,
  editSellerPreference,
  reviewSellerPreference,
  setSellerPreferenceState,
} from "../lib/api";
import { formatDateTime } from "../lib/format";
import { adminKeys, sellerPreferencesQuery } from "../lib/queries";
import { useWriteAccess } from "../lib/use-access";
import "./searches.css";
import "./talent-declines.css";
import "./talent-lists.css";

/** The three views of the same table: what needs a decision, what is live because of one,
 *  and what we turned down. Kept as one page rather than three tabs because the question an
 *  operator asks after approving is "what else is on", and that is one click away here. */
const VIEWS = [
  { value: "awaiting_review", label: "Waiting on us" },
  { value: "active", label: "Live" },
  { value: "rejected", label: "Rejected" },
] as const;

type View = (typeof VIEWS)[number]["value"];

const PREF_SORT = {
  asked: (r: SellerPreferenceRow) => r.talentApprovedAt ?? r.createdAt ?? null,
  talent: (r: SellerPreferenceRow) => r.sellerId ?? r.fiverrSellerId,
  minimum: (r: SellerPreferenceRow) => r.minOfferCents ?? r.minHourlyCents ?? null,
} as const;

/** Whole dollars, because that is the unit a talent states them in and the unit the floor is
 *  compared in. A dash for "not set", never "$0": zero is not a floor anywhere in this
 *  feature, and rendering one would read as "they will work for nothing". */
function money(cents: number | null | undefined): string {
  return cents && cents > 0 ? `$${Math.round(cents / 100).toLocaleString()}` : "-";
}

/** Every numeric boundary on one row, each labelled with what it is compared AGAINST. The
 *  labels are load-bearing: a project floor and an hourly floor are different promises, and
 *  an operator approving "$500" needs to know whether that is a project or an hour. */
function Minimums({ row }: { row: SellerPreferenceRow }) {
  const leaves = Object.entries(row.leafMinimumLabels ?? {}) as [string, number][];
  if (!row.minOfferCents && !row.minHourlyCents && leaves.length === 0) {
    return <span className="tm-unknown">No price minimum</span>;
  }
  return (
    <div className="tp-minimums">
      {row.minOfferCents ? <span>Project: {money(row.minOfferCents)}</span> : null}
      {row.minHourlyCents ? <span>Hourly: {money(row.minHourlyCents)} per hour</span> : null}
      {leaves.map(([label, cents]) => (
        <span key={label} className="rc-cell-sub">
          {label}: {money(cents)}
        </span>
      ))}
    </div>
  );
}

/** Cents to the whole-dollar string the form edits in. Dollars because that is the unit a
 *  talent states a floor in and the unit the row renders; an operator retyping 50000 to fix
 *  "$500 an hour" is how a second misread gets made. */
function dollars(cents: number | null | undefined): string {
  return cents && cents > 0 ? String(Math.round(cents / 100)) : "";
}

/** The form's dollar string back to cents. Blank (or anything unreadable) is null: clearing
 *  the field is how an operator says the talent never stated that floor, and it has to reach
 *  the column as NULL rather than as a zero the writer would have to guess about. */
function centsFrom(value: string): number | null {
  const n = Number(value.trim());
  return value.trim() !== "" && Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
}

function hourFrom(value: string): number | null {
  const n = Number(value.trim());
  return value.trim() !== "" && Number.isInteger(n) ? n : null;
}

type Draft = {
  minOffer: string;
  minHourly: string;
  leaves: Record<string, string>;
  availableFrom: string;
  hoursStart: string;
  hoursEnd: string;
  rules: string;
};

function draftFrom(row: SellerPreferenceRow): Draft {
  return {
    minOffer: dollars(row.minOfferCents),
    minHourly: dollars(row.minHourlyCents),
    leaves: Object.fromEntries(
      Object.entries(row.leafMinimums ?? {}).map(([id, cents]) => [id, dollars(cents)]),
    ),
    availableFrom: row.availableFrom ?? "",
    hoursStart:
      row.workingHoursStart === null || row.workingHoursStart === undefined
        ? ""
        : String(row.workingHoursStart),
    hoursEnd:
      row.workingHoursEnd === null || row.workingHoursEnd === undefined
        ? ""
        : String(row.workingHoursEnd),
    rules: (row.rules ?? []).join("\n"),
  };
}

/** What one queued capture SAYS, in a form, before anybody rules on whether it applies.
 *
 *  The whole row is submitted every time, so a field the operator empties is stored as "the
 *  talent did not say this". That is deliberate and it is the correction a merge cannot
 *  express: the failure this exists to fix is a capture that recorded a boundary the talent
 *  never set, and there is no way to take one back by adding to it.
 *
 *  Leaf minimums are shown by their taxonomy NAME and only for leaves the capture already
 *  carries: the amount is editable, the key is not. Adding a leaf here would mean picking a
 *  sub-category the talent never mentioned, which is authoring a boundary rather than
 *  correcting one. Clearing an amount drops that leaf. */
function PreferenceEditor({
  row,
  busy,
  error,
  onCancel,
  onSave,
}: {
  row: SellerPreferenceRow;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (body: SellerPreferenceEditRequest) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(row));
  const labels = row.leafMinimumLabels ?? {};
  // `leafMinimums` is keyed on the sub-category id and `leafMinimumLabels` on the resolved
  // name, in the same order, so the id keeps the pairing while the operator reads the name.
  const leafNames = Object.keys(labels);
  const leafIds = Object.keys(row.leafMinimums ?? {});

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const submit = () => {
    const leafMinimums: Record<string, number> = {};
    leafIds.forEach((id) => {
      const cents = centsFrom(draft.leaves[id] ?? "");
      if (cents !== null) leafMinimums[id] = cents;
    });
    onSave({
      preferenceId: row.id,
      minOfferCents: centsFrom(draft.minOffer),
      minHourlyCents: centsFrom(draft.minHourly),
      leafMinimums,
      availableFrom: draft.availableFrom.trim() || null,
      workingHoursStart: hourFrom(draft.hoursStart),
      workingHoursEnd: hourFrom(draft.hoursEnd),
      rules: draft.rules
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    });
  };

  return (
    <div className="tp-edit">
      <label className="tp-edit__field">
        <span className="tp-edit__label">Project floor (USD)</span>
        <Input
          inputSize="sm"
          type="number"
          min={0}
          value={draft.minOffer}
          disabled={busy}
          onChange={(e) => set({ minOffer: e.target.value })}
        />
      </label>
      <label className="tp-edit__field">
        <span className="tp-edit__label">Hourly floor (USD per hour)</span>
        <Input
          inputSize="sm"
          type="number"
          min={0}
          value={draft.minHourly}
          disabled={busy}
          onChange={(e) => set({ minHourly: e.target.value })}
        />
      </label>
      {leafIds.map((id, i) => (
        <label className="tp-edit__field" key={id}>
          <span className="tp-edit__label">{leafNames[i] ?? `Category ${id}`} (USD)</span>
          <Input
            inputSize="sm"
            type="number"
            min={0}
            value={draft.leaves[id] ?? ""}
            disabled={busy}
            onChange={(e) => set({ leaves: { ...draft.leaves, [id]: e.target.value } })}
          />
        </label>
      ))}
      <label className="tp-edit__field">
        <span className="tp-edit__label">Available from</span>
        <Input
          inputSize="sm"
          type="date"
          value={draft.availableFrom}
          disabled={busy}
          onChange={(e) => set({ availableFrom: e.target.value })}
        />
      </label>
      {/* Half-open [start, end), hours 0-24, and both or neither: the storage CHECK
          (migration 0179) refuses a wrapping window, so the form cannot offer one. */}
      <div className="tp-edit__field">
        <span className="tp-edit__label">Working hours (0-24, end after start)</span>
        <div className="tp-edit__hours">
          <Input
            inputSize="sm"
            type="number"
            min={0}
            max={23}
            aria-label="Working hours start"
            value={draft.hoursStart}
            disabled={busy}
            onChange={(e) => set({ hoursStart: e.target.value })}
          />
          <span aria-hidden="true">to</span>
          <Input
            inputSize="sm"
            type="number"
            min={1}
            max={24}
            aria-label="Working hours end"
            value={draft.hoursEnd}
            disabled={busy}
            onChange={(e) => set({ hoursEnd: e.target.value })}
          />
        </div>
      </div>
      <label className="tp-edit__field tp-edit__rules">
        <span className="tp-edit__label">Work to skip, one rule per line</span>
        <textarea
          className="rc-input rc-input--sm"
          rows={3}
          value={draft.rules}
          disabled={busy}
          onChange={(e) => set({ rules: e.target.value })}
        />
      </label>
      <div className="tp-edit__foot">
        <Button
          type="button"
          size="sm"
          variant="solid"
          tone="neutral"
          disabled={busy}
          onClick={submit}
        >
          Save correction
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          tone="neutral"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </Button>
        {error ? <span className="tp-edit__error">{error}</span> : null}
      </div>
    </div>
  );
}

/** The two boundaries that are not about money or the kind of work (migrations 0177/0179),
 *  and the reason they are on the row at all: an operator ruling on a capture has to see
 *  everything it will enforce, and until now these two were enforced while invisible here.
 *
 *  They are labelled by what they DO, because they do different things. A start date is
 *  SCREENED: a talent who cannot start before the client needs delivery is dropped from the
 *  search outright. Working hours are not screened, on purpose - filtering on them would
 *  delete the Americas from every afternoon search. They add ranking points and hold the
 *  first outbound message until the window opens, so the talent is always still found. */
function Availability({ row }: { row: SellerPreferenceRow }) {
  const start = row.workingHoursStart;
  const end = row.workingHoursEnd;
  const hasHours = start !== null && start !== undefined && end !== null && end !== undefined;
  if (!row.availableFrom && !hasHours) {
    return <span className="tm-unknown">Nothing set</span>;
  }
  return (
    <div className="tp-minimums">
      {row.availableFrom ? <span>Free from {row.availableFrom}</span> : null}
      {hasHours ? (
        <span className="rc-cell-sub">
          Works {String(start).padStart(2, "0")}:00 to {String(end).padStart(2, "0")}:00
        </span>
      ) : null}
    </div>
  );
}

export function TalentPreferencesPage() {
  const qc = useQueryClient();
  const [view, setView] = useState<View>("awaiting_review");
  const preferences = useInfiniteQuery(sellerPreferencesQuery(view));
  const [pending, setPending] = useState<{ row: SellerPreferenceRow; approved: boolean } | null>(
    null,
  );
  // The decided views' take-backs: unapprove a Live row, reapprove a rejected one. Held
  // apart from `pending` because the two dialogs say different things about different
  // states, and only one can be open at a time anyway (each is opened by a row button).
  const [transition, setTransition] = useState<{
    row: SellerPreferenceRow;
    action: "unapprove" | "reapprove";
  } | null>(null);
  // Reading the queue is `talent_management` at read; RULING on a row is `full`. A read-only
  // holder is meant to watch the worklist, so the page loads for them and only the decision
  // column is dropped.
  const { canWrite: canReview, known: accessKnown } = useWriteAccess("talent_management");

  // The row whose correction form is open, and the last refusal from it. One at a time: the
  // form is a full copy of the row it edits, and two open at once invites an operator to
  // save one believing they had edited the other.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // All three views move on one decision: the row leaves one list and joins another.
  // Invalidating just the current view would leave the other two showing a decision that
  // has already been made.
  const invalidateViews = () => {
    for (const v of VIEWS) {
      void qc.invalidateQueries({ queryKey: adminKeys.sellerPreferences(v.value) });
    }
  };

  const review = useMutation({
    mutationFn: reviewSellerPreference,
    onSuccess: invalidateViews,
  });

  const stateChange = useMutation({
    mutationFn: setSellerPreferenceState,
    onSuccess: invalidateViews,
  });

  // A correction is a write on a row that has NOT been ruled on, so the queue must reload
  // before anybody approves it: approving a stale row would approve what the capture said,
  // not what the operator corrected it to. The refusals are worth showing verbatim rather
  // than as "something went wrong": 404 means somebody else already ruled on this row while
  // it was open, and 422 names the field the writer would not take.
  const edit = useMutation({
    mutationFn: editSellerPreference,
    onSuccess: () => {
      setEditingId(null);
      setEditError(null);
      invalidateViews();
    },
    onError: (error: unknown) => {
      setEditError(
        error instanceof ApiError
          ? (error.detail ?? error.message)
          : "The correction was not saved.",
      );
    },
  });

  // Every page loaded so far, flattened. SORTING reaches these and no further: the queue is
  // keyset-paged newest-first, so a column sort reorders what is loaded rather than the whole
  // status. The toolbar count says so rather than letting "3 rows" read as "3 in the queue".
  const rows = useMemo<SellerPreferenceRow[]>(
    () => preferences.data?.pages.flatMap((p) => p.preferences ?? []) ?? [],
    [preferences.data],
  );
  const sorted = useTableSort(rows, PREF_SORT, null, { restore: "talent-preferences/sort" });
  const paged = usePagedRows(sorted.rows, { restore: "talent-preferences/window" });

  if (preferences.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="talent_management"
          error={preferences.error}
          onRetry={() => void preferences.refetch()}
        />
      </div>
    );
  }

  // Counted server-side and BOUNDED, so a backlog past the cap reads "500+" rather than a
  // number that would quietly be the ceiling (root §1.16).
  const first = preferences.data?.pages[0];
  const waiting = first?.pendingCount ?? 0;
  const waitingLabel = `${waiting.toLocaleString()}${first?.pendingCountCapped ? "+" : ""}`;

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Talent preferences</h1>
        <p className="ad-page__sub">
          Standing boundaries talents set through Mira: a minimum project size, an hourly floor, a
          price for one kind of work, or work to skip entirely. Nothing here filters a search until
          it is approved.
          {accessKnown
            ? canReview
              ? " Read the conversation before you approve one: it is the talent's own words that decide whether we read them right."
              : " Somebody with Full access has to approve one before it takes effect."
            : null}
        </p>
      </header>

      <SectionTabs label="Talent Management sections" tabs={TALENT_TABS} />

      <div className="rc-toolbar sr-toolbar">
        <div className="tp-views" role="group" aria-label="Which preferences to show">
          {VIEWS.map((v) => (
            <Button
              key={v.value}
              type="button"
              size="sm"
              variant={view === v.value ? "solid" : "outline"}
              tone="neutral"
              onClick={() => setView(v.value)}
            >
              {v.label}
            </Button>
          ))}
        </div>
        {preferences.data ? (
          <span className="rc-toolbar__count">
            {waitingLabel} waiting on us
            {rows.length > 0
              ? `, ${rows.length.toLocaleString()} loaded${preferences.hasNextPage ? ", more available" : ""}`
              : ""}
          </span>
        ) : null}
      </div>

      {preferences.isPending ? (
        <TableSkeleton columns={canReview ? 7 : 6} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={view === "awaiting_review" ? "Nothing waiting" : "Nothing here yet"}
          message={
            view === "awaiting_review"
              ? "When a talent sets a standing preference and approves it, it appears here for review."
              : "Preferences show up here once they have been decided."
          }
        />
      ) : (
        <>
          <div className="rc-table-wrap">
            <table className="rc-table">
              <TableExport name="talent-preferences" label="preference captures" />
              <thead>
                <tr>
                  <SortableTh sortKey="asked" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Asked for
                  </SortableTh>
                  <SortableTh sortKey="talent" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Talent
                  </SortableTh>
                  <SortableTh sortKey="minimum" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Minimums
                  </SortableTh>
                  <th>Work to skip</th>
                  <th>Availability</th>
                  <th>State</th>
                  {canReview ? (
                    <th>
                      <span className="ad-visually-hidden">Decision</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {paged.shown.map((row) => (
                  <Fragment key={row.id}>
                    <tr>
                      <td>{formatDateTime(row.talentApprovedAt ?? row.createdAt ?? null)}</td>
                      <td>
                        {/* Straight into the STERLING thread this came out of. `project` is
                          what opens that specific conversation rather than a list of every
                          thread the talent has ever had. */}
                        <ExplorerTalentLink
                          sellerId={row.sellerId ?? row.fiverrSellerId}
                          projectId={row.projectId ?? undefined}
                          className="ex-primary-link"
                        >
                          {row.sellerId ?? row.fiverrSellerId}
                        </ExplorerTalentLink>
                        {row.projectId ? (
                          <span className="rc-cell-sub">Read the conversation</span>
                        ) : (
                          // The thread is a weak reference by design (0140): a standing
                          // preference outlives the project it was set on, so a deleted one
                          // must not erase the boundary.
                          <span className="tm-unknown">Conversation unavailable</span>
                        )}
                      </td>
                      <td>
                        <Minimums row={row} />
                      </td>
                      <td>
                        {(row.rules ?? []).length > 0 ? (
                          <ul className="tp-rules">
                            {(row.rules ?? []).map((rule: string) => (
                              <li key={rule}>{rule}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="tm-unknown">Nothing</span>
                        )}
                      </td>
                      <td>
                        <Availability row={row} />
                      </td>
                      <td>
                        {row.status === "active" ? (
                          <Badge
                            tone="success"
                            size="sm"
                            title={
                              row.reviewedBy
                                ? `Filtering searches. Approved by ${row.reviewedBy}.`
                                : "Filtering searches."
                            }
                          >
                            Live
                          </Badge>
                        ) : row.status === "rejected" ? (
                          <Badge
                            tone="neutral"
                            size="sm"
                            title={row.reviewNote ?? "Turned down; nothing was changed."}
                          >
                            Rejected
                          </Badge>
                        ) : (
                          <Badge tone="warning" size="sm" title="Filters nothing until approved.">
                            Waiting on us
                          </Badge>
                        )}
                      </td>
                      {canReview ? (
                        <td>
                          {view === "awaiting_review" ? (
                            <div className="tp-actions">
                              <Button
                                type="button"
                                variant="outline"
                                tone="neutral"
                                size="sm"
                                onClick={() => setPending({ row, approved: true })}
                              >
                                Approve
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                tone="neutral"
                                size="sm"
                                onClick={() => setPending({ row, approved: false })}
                              >
                                Reject
                              </Button>
                              {/* The third verb. Review is otherwise binary, so an operator
                                who reads the conversation and finds ONE field misread had
                                to reject the whole capture, losing a boundary the talent
                                really did set and was already told we saved. */}
                              <Button
                                type="button"
                                variant="ghost"
                                tone="neutral"
                                size="sm"
                                onClick={() => {
                                  setEditError(null);
                                  setEditingId(editingId === row.id ? null : row.id);
                                }}
                              >
                                {editingId === row.id ? "Close" : "Edit"}
                              </Button>
                            </div>
                          ) : view === "active" ? (
                            <div className="tp-actions">
                              <Button
                                type="button"
                                variant="outline"
                                tone="neutral"
                                size="sm"
                                onClick={() => setTransition({ row, action: "unapprove" })}
                              >
                                Unapprove
                              </Button>
                            </div>
                          ) : (
                            <div className="tp-actions">
                              <Button
                                type="button"
                                variant="outline"
                                tone="neutral"
                                size="sm"
                                onClick={() => setTransition({ row, action: "reapprove" })}
                              >
                                Reapprove
                              </Button>
                            </div>
                          )}
                        </td>
                      ) : null}
                    </tr>
                    {canReview && editingId === row.id ? (
                      <tr>
                        <td colSpan={7}>
                          <PreferenceEditor
                            row={row}
                            busy={edit.isPending}
                            error={editError}
                            onCancel={() => {
                              setEditingId(null);
                              setEditError(null);
                            }}
                            onSave={(body) => edit.mutate(body)}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {/* Both backlogs behind one control: the rows fetched but not painted, then the
              pages still on the server. The sentinel inside it advances on scroll, so the
              queue reads as one continuous list while every request stays a bounded page. */}
          <ShowMoreRow
            remaining={paged.remaining}
            onClick={paged.showMore}
            hasMore={preferences.hasNextPage}
            onLoadMore={() => void preferences.fetchNextPage()}
            loading={preferences.isFetchingNextPage}
            label="preferences"
          />
        </>
      )}

      {/* Says what the TALENT will experience, not which column moves. Both halves matter:
          approving takes work away from somebody, and rejecting leaves them believing a
          boundary is coming. */}
      <ConfirmDialog
        open={pending !== null}
        title={pending?.approved ? "Switch this preference on?" : "Reject this preference?"}
        message={
          pending
            ? pending.approved
              ? `${pending.row.sellerId ?? pending.row.fiverrSellerId} will stop being shortlisted for briefs that do not meet what they asked for. They will not see which briefs those were.`
              : `Nothing changes for ${pending.row.sellerId ?? pending.row.fiverrSellerId}. Anything they already had switched on stays on, and they are not told this was turned down.`
            : ""
        }
        confirmLabel={pending?.approved ? "Switch it on" : "Reject"}
        busy={review.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return;
          review.mutate(
            { preferenceId: pending.row.id, approved: pending.approved },
            { onSettled: () => setPending(null) },
          );
        }}
      />

      {/* The decided views' take-backs. Same rule as above: say what changes in the
          talent's terms, factually about STATE (which row is marked live), and that
          nobody tells them. */}
      <ConfirmDialog
        open={transition !== null}
        title={
          transition?.action === "unapprove"
            ? "Take this preference off?"
            : "Make this preference live again?"
        }
        message={
          transition
            ? transition.action === "unapprove"
              ? `This preference will no longer be marked active for ${transition.row.sellerId ?? transition.row.fiverrSellerId}. It moves to the rejected list, nothing replaces it, and they are not told.`
              : `This becomes the one preference marked active for ${transition.row.sellerId ?? transition.row.fiverrSellerId}. Anything they currently have live is replaced, and they are not told.`
            : ""
        }
        confirmLabel={transition?.action === "unapprove" ? "Take it off" : "Make it live"}
        busy={stateChange.isPending}
        onCancel={() => setTransition(null)}
        onConfirm={() => {
          if (!transition) return;
          stateChange.mutate(
            { preferenceId: transition.row.id, action: transition.action },
            { onSettled: () => setTransition(null) },
          );
        }}
      />
    </div>
  );
}
